import {
  onBeforeMount,
  onBeforeUnmount,
  provide,
  inject,
  ref,
  watch,
  type InjectionKey,
  type Ref
} from 'vue'
import { emitter } from '@/plugins/emitter'
import {
  addHistoryListItem,
  moveHistoryListItem,
  type HistoryColumns,
  type HistoryListItem
} from '@/utils/format-edit/historyList'
import type { RirekishoFormatData } from '@/composables/format-edit/useRirekishoFormatData'

const DEACTIVATE_HISTORY_ITEM = 'deactivate-history-item'

type AutoHistoryList = Record<
  string,
  { year: number; month: number; desc: string }
>

export type RirekishoHistories = {
  activeHistoryItem: Ref<string>
  setEducationalBackgrounds: (historyList: AutoHistoryList) => void
  setJobHistories: (historyList: AutoHistoryList) => void
  resetEducationalBackgrounds: () => void
  resetJobHistories: () => void
  addEducationalHistory: (
    index?: number | null,
    columns?: HistoryColumns
  ) => void
  deleteEducationalHistory: (index: number) => void
  moveEducationalHistory: (params: { index: number; to: string }) => void
  addJobHistory: (index?: number | null, columns?: HistoryColumns) => void
  deleteJobHistory: (index: number) => void
  moveJobHistory: (params: { index: number; to: string }) => void
  addLicenseHistory: (index?: number | null, columns?: HistoryColumns) => void
  deleteLicenseHistory: (index: number) => void
  moveLicenseHistory: (params: { index: number; to: string }) => void
  activateHistoryItem: (id: string) => void
  deactivateHistoryItem: () => void
}

const RIREKISHO_HISTORIES_KEY: InjectionKey<RirekishoHistories> =
  Symbol('rirekishoHistories')

export function provideRirekishoHistories(histories: RirekishoHistories) {
  provide(RIREKISHO_HISTORIES_KEY, histories)
}

export function useRirekishoHistoriesApi(): RirekishoHistories {
  const histories = inject(RIREKISHO_HISTORIES_KEY)
  if (!histories) {
    throw new Error(
      'useRirekishoHistoriesApi() must be used under RirekishoEdit'
    )
  }
  return histories
}

function historySort(a: HistoryListItem, b: HistoryListItem) {
  let aYear = Number(a.year)
  let aMonth = Number(a.month)
  let bYear = Number(b.year)
  let bMonth = Number(b.month)
  if (aYear === bYear && aMonth === bMonth) return 0
  if (!aYear) aYear = 9999
  if (!aMonth) aMonth = 99
  if (!bYear) bYear = 9999
  if (!bMonth) bMonth = 99
  if (aYear > bYear) return 1
  if (aYear < bYear) return -1
  if (aMonth > bMonth) return 1
  if (aMonth < bMonth) return -1
  return 0
}

function latestDescription(list: HistoryListItem[] | undefined) {
  if (!list || list.length === 0) return ''
  const sorted = list.slice().sort(historySort)
  const valid = sorted.filter((item) => {
    const year = Number(item.year)
    const month = Number(item.month)
    return year && month
  })
  const latest = valid.length > 0 ? valid[valid.length - 1] : null
  return latest && latest.description ? latest.description : ''
}

export function useRirekishoHistories(
  formatData: RirekishoFormatData
): RirekishoHistories {
  const activeHistoryItem = ref('')

  function addEducationalHistory(
    index?: number | null,
    columns?: HistoryColumns
  ) {
    if (formatData.educationalHistories == null) {
      formatData.educationalHistories = []
    }
    addHistoryListItem(formatData.educationalHistories, index, columns)
  }

  function addJobHistory(index?: number | null, columns?: HistoryColumns) {
    if (formatData.jobHistories == null) {
      formatData.jobHistories = []
    }
    addHistoryListItem(formatData.jobHistories, index, columns)
  }

  function addLicenseHistory(index?: number | null, columns?: HistoryColumns) {
    if (formatData.licenseHistories == null) {
      formatData.licenseHistories = []
    }
    addHistoryListItem(formatData.licenseHistories, index, columns)
  }

  function setEducationalBackgrounds(historyList: AutoHistoryList) {
    formatData.educationalHistories = []
    let index = 0
    for (const key in historyList) {
      const item = historyList[key]
      addEducationalHistory(index++, {
        year: item.year,
        month: item.month,
        description: item.desc
      })
    }
  }

  function setJobHistories(historyList: AutoHistoryList) {
    for (const key in historyList) {
      const item = historyList[key]
      addJobHistory(null, {
        year: item.year,
        month: item.month,
        description: item.desc
      })
    }
  }

  function resetEducationalBackgrounds() {
    formatData.educationalHistories = []
  }

  function resetJobHistories() {
    formatData.jobHistories = []
  }

  function deleteEducationalHistory(index: number) {
    formatData.educationalHistories.splice(index, 1)
  }

  function deleteJobHistory(index: number) {
    formatData.jobHistories.splice(index, 1)
  }

  function deleteLicenseHistory(index: number) {
    formatData.licenseHistories.splice(index, 1)
  }

  function moveEducationalHistory(params: { index: number; to: string }) {
    moveHistoryListItem(formatData.educationalHistories, params)
  }

  function moveJobHistory(params: { index: number; to: string }) {
    moveHistoryListItem(formatData.jobHistories, params)
  }

  function moveLicenseHistory(params: { index: number; to: string }) {
    moveHistoryListItem(formatData.licenseHistories, params)
  }

  function activateHistoryItem(id: string) {
    activeHistoryItem.value = id
    const element = document.getElementById(id)
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' })
    }
  }

  function deactivateHistoryItem() {
    activeHistoryItem.value = ''
  }

  watch(
    () => formatData.educationalHistories,
    () => {
      formatData.lastestEdu = latestDescription(formatData.educationalHistories)
    },
    { deep: true, immediate: true }
  )

  watch(
    () => formatData.jobHistories,
    () => {
      formatData.lastestJob = latestDescription(formatData.jobHistories)
    },
    { deep: true, immediate: true }
  )

  onBeforeMount(() => {
    emitter.on(DEACTIVATE_HISTORY_ITEM, deactivateHistoryItem)
  })

  onBeforeUnmount(() => {
    emitter.off(DEACTIVATE_HISTORY_ITEM, deactivateHistoryItem)
  })

  return {
    activeHistoryItem,
    setEducationalBackgrounds,
    setJobHistories,
    resetEducationalBackgrounds,
    resetJobHistories,
    addEducationalHistory,
    deleteEducationalHistory,
    moveEducationalHistory,
    addJobHistory,
    deleteJobHistory,
    moveJobHistory,
    addLicenseHistory,
    deleteLicenseHistory,
    moveLicenseHistory,
    activateHistoryItem,
    deactivateHistoryItem
  }
}
